export type EstadoConector = 'desconectado' | 'conectado' | 'expirado' | 'erro';

export interface CampoConector {
  nome: string;
  secreto: boolean;
  obrigatorio: boolean;
  tipo: 'texto' | 'selecao';
  opcoes: string[];
  gerar: boolean;
  configurado: boolean;
  ultimos4: string | null;
  valor: string | null;
}

export interface Conector {
  tipo: string;
  estado: EstadoConector;
  campos: CampoConector[];
  oauth: { disponivel: boolean; redirect_uri: string } | null;
  /** Trilha Conta Azul: OAuth com o app do cliente (endereço de retorno a cadastrar no app e se já foi autorizado). */
  autorizacao?: { redirect_uri: string; autorizado: boolean } | null;
  sem_credencial: boolean;
  webhook_url: string | null;
  habilitados: string[];
  ultimo_teste: string | null;
  ultimo_erro: string | null;
  /** Fase 2 (W26): Google Calendar, quantos profissionais ou recursos têm agenda escolhida. */
  sincronizados?: number | null;
  /** Fase 2 (W30): WhatsApp não oficial (Baileys ou whatsmeow), pareado por QR ou código. */
  /** W40: `ate_ms` é o fim do bloqueio temporário ou da restrição da conta. */
  pareamento?: { provedor: 'baileys' | 'whatsmeow'; configurado: boolean; sessao_estado: string | null; numero: string | null; ate_ms?: number | null } | null;
  /** Fase 2 (W33, item 0.7): Resend, o servidor tem a chave da plataforma (sem o conector próprio, o envio sai por ela). */
  envio_plataforma?: boolean | null;
  /** E2: o conector aceita ser só de uma empresa; e o que vale para a empresa atual é o dela (`empresa`) ou o de todas. */
  por_empresa?: boolean;
  empresa?: boolean;
  /** U14 F3: WhatsApp pela Zernio (número conectado, perfil do workspace já criado e chave e segredo salvos). */
  zernio?: { numero: string | null; perfil: boolean; pronto: boolean } | null;
  /** U14 F6: Maton (chave e segredo salvos; gatilhos já criados pelas automações). */
  maton?: { pronto: boolean; gatilhos: number } | null;
}

/** Fase 2 (W30): estado da sessão do WhatsApp não oficial (QR e código só enquanto valem). */
export interface EstadoPareamento {
  provedor: 'baileys' | 'whatsmeow';
  estado: 'nao_configurado' | 'gateway_fora' | 'sem_sessao' | 'iniciando' | 'aguardando_qr' | 'pareado' | 'desconectado' | 'banido' | 'encerrada' | 'restrito';
  qr: string | null;
  codigo: string | null;
  numero: string | null;
  motivo: string | null;
  ate_ms?: number | null;
}

export interface ResultadoTeste {
  estado: 'conectado' | 'erro' | 'expirado';
  erro: string | null;
}

export interface Assinatura {
  id: string;
  nome: string;
  url: string;
  eventos: string[];
  estado: 'ativa' | 'pausada' | 'desativada';
  falhas_seguidas: number;
  entregas_total: number;
  entregas_pendentes: number;
  ultima_entrega: string | null;
  ultimo_status: number | null;
  created_at: string;
  /**
   * Review r11 spec (B3): eventos de módulo desinstalado. O catálogo não os mostra; a tela os lista para tirar. Renomear e tirar
   * são livres; trocar a URL ou reativar com eles é 422 `webhook.eventos_orfaos`.
   */
  eventos_orfaos?: string[];
}

export interface EventoCatalogo {
  evento: string;
  modulo: string;
  modulo_nome: string;
  /** Review r10 segurança (M1): quem não lê o módulo dono do evento não pode assiná-lo (o servidor recusa). */
  assinavel?: boolean;
}

export interface Entrega {
  id: string;
  assinatura_id: string;
  evento: string;
  evento_id: string;
  payload: unknown;
  tentativa: number;
  estado: 'pendente' | 'entregue' | 'falhou' | 'cancelada';
  status_http: number | null;
  resposta: string | null;
  proxima_tentativa: string | null;
  /** Review r11 segurança (M1): por que o worker cancelou a entrega sem POST (texto gravado em pt; a tela traduz). */
  motivo?: string | null;
  /** Review r12 robustez (M1): entrega já entregue que esta reenvia (o `evento_id` é o dela); null fora do reenvio. */
  reenvio_de?: string | null;
  created_at: string;
  updated_at: string;
  /** Review r10 segurança (M1): `conteudo` (payload e resposta nulos) ou `campos` (campo confidencial fora) pela permissão de quem lê. */
  ocultado?: 'conteudo' | 'campos' | null;
}

export interface EventoRecebido {
  id: string;
  conector: string;
  id_externo: string;
  payload: unknown;
  estado: 'recebido' | 'processado' | 'erro' | 'ignorado';
  erro: string | null;
  created_at: string;
  /** Review r38 segurança (M1): `conteudo` (payload e erro nulos) sem leitura do módulo dono do conector ou da empresa dele. */
  ocultado?: 'conteudo' | 'campos' | null;
}

export interface Chave {
  id: string;
  nome: string;
  prefixo: string;
  escopos: string[];
  /** Nulo na chave de agente (fase 2, W11): vale o papel do dono. */
  papel_id: string | null;
  papel_nome: string | null;
  expira_em: string | null;
  expirada: boolean;
  ultimo_uso: string | null;
  estado: 'ativa' | 'revogada';
  created_at: string;
  /** Fase 2 (W11): chave de um assistente pessoal (o dono gera e rotaciona em Minha conta > Meus assistentes). */
  assistente_id?: string | null;
  assistente_nome?: string | null;
  dono_nome?: string | null;
}

export interface OpcoesChave {
  papeis: { id: string; nome: string }[];
  modulos: { slug: string; nome: string }[];
}

export interface ChamadaApi {
  id: string;
  chave_id: string;
  chave_nome: string | null;
  chave_prefixo: string | null;
  metodo: string;
  rota: string;
  status: number;
  latencia_ms: number;
  ip: string | null;
  created_at: string;
}

export interface ToolMcp {
  nome: string;
  modulo: string;
  modulo_nome: string;
  descricao: string;
  tipo: 'leitura' | 'escrita';
  ligada: boolean;
}

export interface InfoMcp {
  url: string;
  estado: 'ativo';
  tools: ToolMcp[];
}

/** Lote 19/10: conexão por OAuth do MCP (GET /integracoes/mcp/oauth). */
export interface ConexaoOAuth {
  id: string;
  cliente_nome: string;
  cliente_id: string;
  usuario_id: string;
  pessoa_nome: string | null;
  pessoa_email: string | null;
  escopos: string[];
  ultimo_uso: string | null;
  criada_em: string;
}

export interface OAuthMcp {
  ligado: boolean;
  conexoes: ConexaoOAuth[];
}

export interface ChamadaMcp {
  id: string;
  chave_id: string;
  chave_nome: string | null;
  chave_prefixo: string | null;
  tool: string;
  argumentos: unknown;
  sucesso: boolean;
  resultado: unknown;
  latencia_ms: number;
  created_at: string;
  /** Review r38 segurança (M1): `conteudo` (argumentos e resultado nulos) sem leitura do módulo da tool ou da empresa da chave. */
  ocultado?: 'conteudo' | 'campos' | null;
}

/**
 * Papéis de fábrica (dono, administrador, gestor, operador, leitura) são traduzidos pelo código;
 * papel personalizado mostra o nome gravado. Quando a API só entrega o nome, o nome de fábrica em português
 * faz o papel de código.
 */
export function nomeDoPapel(t: (chave: string, opcoes?: { defaultValue?: string }) => string, p: { nome: string; codigo?: string; de_fabrica?: boolean }): string {
  if (p.de_fabrica === false) return p.nome;
  const codigo = p.codigo ?? p.nome.trim().toLowerCase();
  return t(`papelFabrica.${codigo}`, { defaultValue: p.nome });
}

/** Trilha Conta Azul (13/10): tela Integrações > Conta Azul. */
export type RecursoContaAzul = 'pessoas' | 'contratos' | 'titulos_receber' | 'titulos_pagar' | 'centros' | 'detalhes';

export interface ResumoContaAzul {
  conector: { estado: EstadoConector; empresa: boolean; ambiente: string | null; redirect_uri: string } | null;
  ve_geral: boolean;
  sincronias: { escopo: string; recurso: RecursoContaAzul; executado_em: string | null; completa_em: string | null; lidos: number; erro: string | null }[];
  espelho: { pessoas: number; contratos: number; titulos_receber: number; titulos_pagar: number; sem_empresa: number; sem_detalhe: number; removidos: number };
  centros: { id_externo: string; codigo: string | null; nome: string; empresa_destino_id: string | null }[];
  empresas: { id: string; nome: string }[];
}

export type ClasseComparacao = 'so_la' | 'so_aqui' | 'divergente' | 'igual';

export interface ItemComparacaoContaAzul {
  classe: ClasseComparacao;
  ligacao?: 'chave' | 'documento' | null;
  diferencas: string[];
  ca: { id_externo: string; nome?: string; pessoa_nome?: string | null; documento_digitos: string | null; vencimento?: string; valor_centavos?: number; status?: string; empresa_inferida?: boolean; email?: string | null } | null;
  malgo: { id: string; numero?: string; nome?: string; parceiro_nome?: string | null; documento_digitos: string | null; vencimento?: string; valor_centavos?: number; estado?: string; email?: string | null } | null;
}

export interface ComparacaoContaAzul {
  entidade: 'titulos' | 'pessoas';
  mes: string | null;
  acesso_malgo: boolean;
  contagens: Record<ClasseComparacao, number>;
  itens: ItemComparacaoContaAzul[];
  truncado: boolean;
  /** Review r54 robustez (B3): um dos lados passou do teto de leitura; contagens parciais. */
  incompleto?: boolean;
}

export interface TotaisContaAzul {
  ano: number;
  acesso_malgo: boolean;
  meses: { mes: string; ca_quantidade: number; ca_valor: number; ca_aberto: number; malgo_quantidade: number | null; malgo_valor: number | null; malgo_aberto: number | null }[];
}

export type EntidadeConversao = 'contas_financeiras' | 'pessoas' | 'titulos_receber' | 'titulos_pagar' | 'contratos';

export interface ConversaoContaAzul {
  entidade: EntidadeConversao;
  importador: { id: string; caminho: string };
  pode_importar: boolean;
  empresa_escolhida: boolean;
  csv: string;
  linhas: number;
  restantes: number;
  avisos: Record<string, number>;
}
